import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, User2 } from "lucide-react";
import ClamaloSpinner from "@/components/ui/ClamaloSpinner";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import JournalEntryCard from "./JournalEntryCard";
import JournalEntryVisibilityCheck from "./JournalEntryVisibilityCheck";
import { JournalEntry } from "@/types/journal";
import { ClientTherapistRelationship } from "@/types/therapist";
import EmotionTrackingGraph from "./EmotionTrackingGraph";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateFormat";

interface JournalEntryListProps {
  selectedClientId: string;
  journalEntries: JournalEntry[] | undefined;
  therapistNotes: { [entryId: string]: string };
  entriesLoading: boolean;
  selectedClient: ClientTherapistRelationship | undefined;
  onReturnToClientList: () => void;
  onNoteChange: (entryId: string, content: string) => void;
  getClientDisplayName: (client?: ClientTherapistRelationship['client']) => string;
  formatDate: (dateString: string) => string;
  onViewAll?: () => void;
}

const JournalEntryList = ({
  selectedClientId,
  journalEntries,
  therapistNotes,
  entriesLoading,
  selectedClient,
  onReturnToClientList,
  onNoteChange,
  getClientDisplayName,
  formatDate,
  onViewAll
}: JournalEntryListProps) => {
  const [savingNotes, setSavingNotes] = useState<Record<string, boolean>>({});
  const [hasVisibilityPermission, setHasVisibilityPermission] = useState<boolean | null>(null);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const navigate = useNavigate();

  const clientName = selectedClient ? getClientDisplayName(selectedClient.client) : "Selected Client";

  const handleSaveNote = async (entryId: string) => {
    try {
      const noteContent = therapistNotes[entryId] || "";
      setSavingNotes(prev => ({ ...prev, [entryId]: true }));

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to save notes");
        return;
      }

      const { data, error } = await supabase.rpc('save_therapist_note', {
        therapist_id_param: user.id,
        client_id_param: selectedClientId,
        entry_id_param: entryId,
        content_param: noteContent
      });

      if (error) throw error;
      
      toast.success("Note saved successfully");
    } catch (error: any) {
      console.error("Error saving note:", error);
      toast.error(error.message || "Failed to save note");
    } finally {
      setSavingNotes(prev => ({ ...prev, [entryId]: false }));
    }
  };

  // Show only the last 5 entries by default
  const entriesToShow = journalEntries ? journalEntries.slice(0, 5) : [];

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#333B44] flex items-center">
            <User2 className="mr-2 h-5 w-5" />
            {clientName}
          </h2>
          <p className="text-muted-foreground">View journal entries and add your notes</p>
        </div>
        <Button 
          variant="outline" 
          onClick={onReturnToClientList}
          className="border-[#CCCCCC] hover:bg-[#E8F0F8] mt-2 sm:mt-0 w-full sm:w-auto"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Return to Client List
        </Button>
      </div>

      <JournalEntryVisibilityCheck
        clientId={selectedClientId}
        onCheckComplete={(hasPermission) => {
          setHasVisibilityPermission(hasPermission);
          setIsCheckingPermission(false);
        }}
      >
        {isCheckingPermission ? (
          <div className="flex items-center justify-center py-12">
            <ClamaloSpinner size={32} />
            <span className="ml-2 text-[#3BAFDA]">Checking permissions...</span>
          </div>
        ) : entriesLoading ? (
          <div className="flex items-center justify-center py-12">
            <ClamaloSpinner size={32} />
          </div>
        ) : !journalEntries || journalEntries.length === 0 ? (
          <>
            <EmotionTrackingGraph journalEntries={[]} entriesLoading={false} />
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>No Journal Entries</CardTitle>
                <CardDescription>
                  This client hasn't created any journal entries yet.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  When they add entries, they will appear here for your review.
                </p>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <EmotionTrackingGraph journalEntries={journalEntries} entriesLoading={false} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 mt-6">
              <h3 className="text-xl font-bold text-[#333B44] text-center sm:text-left w-full">Last Five Journal Entries</h3>
              {onViewAll && (
                <Button
                  variant="outline"
                  onClick={onViewAll}
                  className="mt-2 sm:mt-0 w-full sm:w-auto sm:ml-4"
                >
                  View All Journal Entries
                </Button>
              )}
            </div>
            {/* Horizontal scroll for last five journal entries */}
            <div className="flex space-x-4 overflow-x-auto pb-2">
              {entriesToShow.map((entry) => (
                <Card key={entry.id} className="min-w-[260px] max-w-[320px] border-[#CCCCCC] overflow-hidden flex-shrink-0">
                  <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg text-[#333B44]">{entry.mood || 'Unspecified Mood'}</CardTitle>
                        <CardDescription>{formatDate(entry.created_at)}</CardDescription>
                      </div>
                      {entry.emotion_type && (
                        <div className="text-right">
                          <div className="font-medium text-[#3BAFDA]">{entry.emotion_type}</div>
                          <div className="text-sm text-muted-foreground">
                            Intensity: {entry.emotion_intensity}/10
                          </div>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap text-left">{entry.content.slice(0, 80)}{entry.content.length > 80 ? '...' : ''}</p>
                    <Button 
                      size="sm" 
                      className="mt-3 w-full" 
                      onClick={() => navigate(`/therapist/client/${selectedClientId}/journal-entries#${entry.id}`)}
                    >
                      View
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </JournalEntryVisibilityCheck>

      {!isCheckingPermission && !hasVisibilityPermission && (
        <Card className="mt-6 border-orange-200 bg-orange-50/50">
          <CardHeader>
            <CardTitle className="text-orange-700">Permission Restricted</CardTitle>
            <CardDescription>
              This client has not given permission to view their journal entries.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-orange-700">
              The client needs to enable journal sharing in their profile settings
              before you can view their entries.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JournalEntryList;
